import {
  CalendarDate,
  GregorianCalendar,
  parseAbsolute,
  PersianCalendar,
  toCalendar,
  toZoned,
} from '@internationalized/date';

import { normalizeDigits, toPersianDigits } from './digits.js';

/**
 * Solar Hijri dates (FTD §3.2, AC §4.3–§4.4). The shared Jalali library is `@internationalized/date`
 * (`PersianCalendar`), chosen in ADR-F001; its leap years are covered by spec tests.
 *
 * - `LocalDate` (`YYYY-MM-DD`, Gregorian, interpreted in Asia/Tehran) is converted calendar to
 *   calendar only. It never passes through a JavaScript `Date` or a time zone, so the result does not
 *   depend on the device time zone.
 * - A timestamp (ISO 8601 UTC instant) is converted to Asia/Tehran before it is displayed.
 */

export const BUSINESS_TIME_ZONE = 'Asia/Tehran';

/** A calendar date without time: `YYYY-MM-DD` in the ISO calendar (AC §4.4). */
export type LocalDate = string;

/** An ISO 8601 UTC instant, for example `2026-09-17T09:15:30.000Z` (AC §4.3). */
export type Timestamp = string;

export interface SolarHijriDate {
  readonly day: number;
  /** 1 (Farvardin) to 12 (Esfand). */
  readonly month: number;
  readonly year: number;
}

export const SOLAR_HIJRI_MONTHS_FA = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

const persian = new PersianCalendar();
const gregorian = new GregorianCalendar();
const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** Parses a `LocalDate`, rejecting impossible dates such as `2025-02-30`. */
export function parseLocalDate(value: LocalDate): CalendarDate {
  const match = LOCAL_DATE_PATTERN.exec(value);

  if (match === null) {
    throw new RangeError(`Not a LocalDate (YYYY-MM-DD): ${value}`);
  }

  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new CalendarDate(gregorian, year, month, day);

  if (date.year !== year || date.month !== month || date.day !== day) {
    throw new RangeError(`Not a valid calendar date: ${value}`);
  }

  return date;
}

/** Converts a `LocalDate` to Solar Hijri without any time-zone conversion. */
export function localDateToSolarHijri(value: LocalDate): SolarHijriDate {
  const converted = toCalendar(parseLocalDate(value), persian);

  return { day: converted.day, month: converted.month, year: converted.year };
}

/** Converts a Solar Hijri date to a `LocalDate`, rejecting days that do not exist (e.g. 1404/12/30). */
export function solarHijriToLocalDate({ year, month, day }: SolarHijriDate): LocalDate {
  if (month < 1 || month > 12 || day < 1) {
    throw new RangeError(`Not a valid Solar Hijri date: ${year}/${month}/${day}`);
  }

  const monthStart = new CalendarDate(persian, year, month, 1);

  if (day > persian.getDaysInMonth(monthStart)) {
    throw new RangeError(`Not a valid Solar Hijri date: ${year}/${month}/${day}`);
  }

  const converted = toCalendar(new CalendarDate(persian, year, month, day), gregorian);

  return `${String(converted.year).padStart(4, '0')}-${pad2(converted.month)}-${pad2(converted.day)}`;
}

/** True when Esfand of the Solar Hijri year has 30 days. */
export function isSolarHijriLeapYear(year: number): boolean {
  return persian.getDaysInMonth(new CalendarDate(persian, year, 12, 1)) === 30;
}

export type DateStyle = 'numeric' | 'long';

function formatSolarHijri(date: SolarHijriDate, style: DateStyle): string {
  if (style === 'long') {
    return toPersianDigits(
      `${date.day} ${SOLAR_HIJRI_MONTHS_FA[date.month - 1] ?? ''} ${date.year}`,
    );
  }

  return toPersianDigits(`${date.year}/${pad2(date.month)}/${pad2(date.day)}`);
}

/**
 * Formats a `LocalDate` (due date, business date, settlement week) in Solar Hijri, with no time-zone
 * conversion: `2025-03-20` → `۱۴۰۳/۱۲/۳۰` or `۳۰ اسفند ۱۴۰۳`.
 */
export function formatLocalDate(value: LocalDate, style: DateStyle = 'numeric'): string {
  return formatSolarHijri(localDateToSolarHijri(value), style);
}

/** The Persian weekdays by `Date#getUTCDay()` (0 = Sunday); the Iranian week starts on Saturday. */
export const WEEKDAYS_FA = [
  'یکشنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنجشنبه',
  'جمعه',
  'شنبه',
] as const;

/** The weekday of a `LocalDate`, from the calendar date alone (no device time zone involved). */
export function weekdayOf(value: LocalDate): (typeof WEEKDAYS_FA)[number] {
  const day = parseLocalDate(value);
  const index = new Date(Date.UTC(day.year, day.month - 1, day.day)).getUTCDay();
  return WEEKDAYS_FA[index] ?? 'شنبه';
}

/**
 * A `LocalDate` with its weekday, as the buyer prototype's home eyebrow: `2026-09-19` →
 * `شنبه ۲۸ شهریور` (with `withYear`, `شنبه ۲۸ شهریور ۱۴۰۵`). Solar Hijri through the shared calendar.
 */
export function formatWeekdayDate(
  value: LocalDate,
  options: { readonly withYear?: boolean } = {},
): string {
  const date = localDateToSolarHijri(value);
  const month = SOLAR_HIJRI_MONTHS_FA[date.month - 1] ?? '';
  const year = options.withYear === true ? ` ${date.year}` : '';
  return toPersianDigits(`${weekdayOf(value)} ${date.day} ${month}${year}`);
}

/**
 * A `LocalDate` as Solar Hijri day and month only, for short labels where the year is clear from
 * context: `2026-10-24` → `۲ آبان`. No time-zone conversion.
 */
export function formatDayMonth(value: LocalDate): string {
  const date = localDateToSolarHijri(value);
  return toPersianDigits(`${date.day} ${SOLAR_HIJRI_MONTHS_FA[date.month - 1] ?? ''}`);
}

export interface TehranDateTime extends SolarHijriDate {
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

/** Converts a UTC instant to the Asia/Tehran wall-clock time on the Solar Hijri calendar. */
export function timestampToTehran(value: Timestamp): TehranDateTime {
  const zoned = toCalendar(parseAbsolute(value, BUSINESS_TIME_ZONE), persian);

  return {
    day: zoned.day,
    hour: zoned.hour,
    minute: zoned.minute,
    month: zoned.month,
    second: zoned.second,
    year: zoned.year,
  };
}

/** The Asia/Tehran business date (`LocalDate`) of a UTC instant. */
export function timestampToBusinessDate(value: Timestamp): LocalDate {
  const zoned = parseAbsolute(value, BUSINESS_TIME_ZONE);

  return `${String(zoned.year).padStart(4, '0')}-${pad2(zoned.month)}-${pad2(zoned.day)}`;
}

export interface TimestampFormatOptions {
  readonly style?: DateStyle;
  /** Show the time of day. Defaults to true. */
  readonly withTime?: boolean;
  /** Append «به وقت تهران» where the time zone matters (operational views). */
  readonly withZone?: boolean;
}

/**
 * Formats an event timestamp in Asia/Tehran: `2025-03-20T20:30:00.000Z` → `۱۴۰۴/۰۱/۰۱، ۰۰:۰۰`.
 */
export function formatTimestamp(value: Timestamp, options: TimestampFormatOptions = {}): string {
  const tehran = timestampToTehran(value);
  const date = formatSolarHijri(tehran, options.style ?? 'numeric');

  if (options.withTime === false) {
    return date;
  }

  const time = toPersianDigits(`${pad2(tehran.hour)}:${pad2(tehran.minute)}`);
  const zone = options.withZone === true ? ' به وقت تهران' : '';

  return `${date}، ${time}${zone}`;
}

/**
 * Parses a Solar Hijri date typed by a person (`۱۴۰۵/۷/۵`, `1405-07-05`, `۱۴۰۵ / ۰۷ / ۰۵`; Persian or
 * Latin digits; spaces ignored) to a `LocalDate`, or `null` when it is not a real date.
 */
export function parseSolarHijriInput(input: string): LocalDate | null {
  const normalized = normalizeDigits(input).replace(/\s+/g, '').replace(/[-.]/g, '/');
  const match = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(normalized);
  if (match === null) {
    return null;
  }
  try {
    return solarHijriToLocalDate({
      day: Number(match[3]),
      month: Number(match[2]),
      year: Number(match[1]),
    });
  } catch {
    return null;
  }
}

/**
 * The UTC instants that bound one Asia/Tehran business day, for timestamp range filters
 * (AC §4.8: `…From` is inclusive and `…To` exclusive).
 */
export function businessDayBounds(value: LocalDate): {
  readonly end: Timestamp;
  readonly start: Timestamp;
} {
  const day = parseLocalDate(value);
  const start = toZoned(day, BUSINESS_TIME_ZONE);
  const end = toZoned(day.add({ days: 1 }), BUSINESS_TIME_ZONE);
  return { end: end.toDate().toISOString(), start: start.toDate().toISOString() };
}

/** The Asia/Tehran business date of an instant (defaults to now), for "today" list presets. */
export function todayBusinessDate(now: Date = new Date()): LocalDate {
  return timestampToBusinessDate(now.toISOString());
}

/**
 * The UTC instants that bound the Iranian business week (Saturday to Friday, Asia/Tehran) that
 * contains `value`, for the «این هفته» list preset (AC §4.8: `…From` inclusive, `…To` exclusive).
 */
export function businessWeekBounds(value: LocalDate): {
  readonly end: Timestamp;
  readonly start: Timestamp;
} {
  const day = parseLocalDate(value);
  // 0 = Sunday … 6 = Saturday, from the calendar date alone (no device time zone involved).
  const weekday = new Date(Date.UTC(day.year, day.month - 1, day.day)).getUTCDay();
  const saturday = day.subtract({ days: (weekday + 1) % 7 });
  const start = toZoned(saturday, BUSINESS_TIME_ZONE);
  const end = toZoned(saturday.add({ days: 7 }), BUSINESS_TIME_ZONE);
  return { end: end.toDate().toISOString(), start: start.toDate().toISOString() };
}
